export type ThemeStudioAssetSource = 'themeStudio' | 'legacy' | 'unit' | 'none';
export type ThemeStudioAssetMode = 'text' | 'logo' | 'unitLogo' | 'customLogo' | 'none';

export interface ThemeStudioAssetReference {
  assetId?: string;
  url?: string;
  source: ThemeStudioAssetSource;
  mode?: ThemeStudioAssetMode;
  createdAt?: string;
  updatedAt?: string;
}

export interface ThemeStudioBackgroundAssetState {
  backgroundImage?: ThemeStudioAssetReference | null;
}

export interface ThemeStudioBrandHeaderAssetState {
  headerLogo?: ThemeStudioAssetReference | null;
  headerBrandMode?: ThemeStudioAssetReference | null;
  headerLogoMode?: ThemeStudioAssetReference | null;
  timeWindowFallbackLogo?: ThemeStudioAssetReference | null;
}

export interface ThemeStudioAssetDraftState {
  background: ThemeStudioBackgroundAssetState;
  brandHeader: ThemeStudioBrandHeaderAssetState;
}

export interface ThemeStudioPublishedAssetSnapshot extends ThemeStudioAssetDraftState {
  snapshotVersion: 1;
  publishedAt?: string;
}

export interface ThemeStudioActiveAssetSnapshot extends ThemeStudioPublishedAssetSnapshot {
  activatedAt?: string;
}

const THEME_STUDIO_ASSET_SOURCES: readonly ThemeStudioAssetSource[] = ['themeStudio', 'legacy', 'unit', 'none'];
const THEME_STUDIO_ASSET_MODES: readonly ThemeStudioAssetMode[] = ['text', 'logo', 'unitLogo', 'customLogo', 'none'];
const ASSET_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isSafeThemeStudioAssetUrl = (value: unknown): value is string =>
  typeof value === 'string'
  && value.trim().length > 0
  && value.length <= 2048
  && !/[<>"{}|\\^`]/.test(value)
  && !/^\s*javascript:/i.test(value);

const normalizeThemeStudioAssetReference = (value: unknown): ThemeStudioAssetReference | null => {
  if (!isRecord(value) || !THEME_STUDIO_ASSET_SOURCES.includes(value.source as ThemeStudioAssetSource)) return null;
  const source = value.source as ThemeStudioAssetSource;
  const mode = THEME_STUDIO_ASSET_MODES.includes(value.mode as ThemeStudioAssetMode)
    ? value.mode as ThemeStudioAssetMode
    : undefined;
  const url = isSafeThemeStudioAssetUrl(value.url) ? value.url.trim() : undefined;
  const assetId = typeof value.assetId === 'string' && ASSET_ID_PATTERN.test(value.assetId) ? value.assetId : undefined;
  const createdAt = typeof value.createdAt === 'string' && ISO_DATE_PATTERN.test(value.createdAt) ? value.createdAt : undefined;
  const updatedAt = typeof value.updatedAt === 'string' && ISO_DATE_PATTERN.test(value.updatedAt) ? value.updatedAt : undefined;

  if (value.url !== undefined && !url) return null;
  if (value.assetId !== undefined && !assetId) return null;
  if (value.createdAt !== undefined && !createdAt) return null;
  if (value.updatedAt !== undefined && !updatedAt) return null;

  return {
    source,
    ...(mode ? { mode } : {}),
    ...(assetId ? { assetId } : {}),
    ...(url ? { url } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
  };
};

type ThemeStudioAssetSlotKey =
  | 'backgroundImage'
  | 'headerLogo'
  | 'headerBrandMode'
  | 'headerLogoMode'
  | 'timeWindowFallbackLogo';

const normalizeSlotReference = (
  slot: ThemeStudioAssetSlotKey,
  reference: ThemeStudioAssetReference | null,
): ThemeStudioAssetReference | null => {
  if (!reference) return null;

  if (slot === 'backgroundImage' && reference.source === 'themeStudio' && !reference.url) return null;
  if (slot === 'headerLogo' && reference.source === 'themeStudio' && reference.mode === 'customLogo' && !reference.url) return null;
  if (slot === 'headerBrandMode') {
    if (reference.mode !== 'text' && reference.mode !== 'logo') return null;
    return { source: reference.source, mode: reference.mode };
  }
  if (slot === 'headerLogoMode') {
    if (reference.mode !== 'unitLogo' && reference.mode !== 'customLogo' && reference.mode !== 'none') return null;
    return { source: reference.source, mode: reference.mode };
  }

  return reference;
};

const normalizeAssetSlot = (container: Record<string, unknown>, key: ThemeStudioAssetSlotKey): ThemeStudioAssetReference | null | undefined => {
  if (!(key in container)) return undefined;
  return normalizeSlotReference(key, normalizeThemeStudioAssetReference(container[key]));
};

export const createEmptyThemeStudioAssetDraftState = (): ThemeStudioAssetDraftState => ({
  background: {},
  brandHeader: {},
});

export const normalizeThemeStudioAssetDraftState = (draftAssets: unknown): ThemeStudioAssetDraftState => {
  const source = isRecord(draftAssets) ? draftAssets : {};
  const background = isRecord(source.background) ? source.background : {};
  const brandHeader = isRecord(source.brandHeader) ? source.brandHeader : {};
  const backgroundImage = normalizeAssetSlot(background, 'backgroundImage');
  const headerLogo = normalizeAssetSlot(brandHeader, 'headerLogo');
  const headerBrandMode = normalizeAssetSlot(brandHeader, 'headerBrandMode');
  const headerLogoMode = normalizeAssetSlot(brandHeader, 'headerLogoMode');
  const timeWindowFallbackLogo = normalizeAssetSlot(brandHeader, 'timeWindowFallbackLogo');

  return {
    background: {
      ...(backgroundImage !== undefined ? { backgroundImage } : {}),
    },
    brandHeader: {
      ...(headerLogo !== undefined ? { headerLogo } : {}),
      ...(headerBrandMode !== undefined ? { headerBrandMode } : {}),
      ...(headerLogoMode !== undefined ? { headerLogoMode } : {}),
      ...(timeWindowFallbackLogo !== undefined ? { timeWindowFallbackLogo } : {}),
    },
  };
};

export const normalizeThemeStudioPublishedAssetSnapshot = (publishedAssets: unknown): ThemeStudioPublishedAssetSnapshot | null => {
  if (!isRecord(publishedAssets) || publishedAssets.snapshotVersion !== 1) return null;
  return {
    snapshotVersion: 1,
    ...normalizeThemeStudioAssetDraftState(publishedAssets),
    ...(typeof publishedAssets.publishedAt === 'string' && ISO_DATE_PATTERN.test(publishedAssets.publishedAt)
      ? { publishedAt: publishedAssets.publishedAt }
      : {}),
  };
};

export const normalizeThemeStudioActiveAssetSnapshot = (activeAssets: unknown): ThemeStudioActiveAssetSnapshot | null => {
  if (!isRecord(activeAssets) || activeAssets.snapshotVersion !== 1) return null;
  return {
    snapshotVersion: 1,
    ...normalizeThemeStudioAssetDraftState(activeAssets),
    ...(typeof activeAssets.publishedAt === 'string' && ISO_DATE_PATTERN.test(activeAssets.publishedAt)
      ? { publishedAt: activeAssets.publishedAt }
      : {}),
    ...(typeof activeAssets.activatedAt === 'string' && ISO_DATE_PATTERN.test(activeAssets.activatedAt)
      ? { activatedAt: activeAssets.activatedAt }
      : {}),
  };
};

export const cloneThemeStudioAssetDraftState = normalizeThemeStudioAssetDraftState;

export const buildThemeStudioPublishedAssetSnapshot = (
  draftAssets: ThemeStudioAssetDraftState | null | undefined,
  publishedAt?: string,
): ThemeStudioPublishedAssetSnapshot => ({
  snapshotVersion: 1,
  ...normalizeThemeStudioAssetDraftState(draftAssets),
  ...(publishedAt ? { publishedAt } : {}),
});

export const buildThemeStudioActiveAssetSnapshot = (
  publishedAssets: ThemeStudioPublishedAssetSnapshot | null | undefined,
  activatedAt?: string,
): ThemeStudioActiveAssetSnapshot => ({
  snapshotVersion: 1,
  ...normalizeThemeStudioAssetDraftState(publishedAssets),
  ...(publishedAssets?.publishedAt ? { publishedAt: publishedAssets.publishedAt } : {}),
  ...(activatedAt ? { activatedAt } : {}),
});
